import { ui, dia } from '@joint/plus';
import { getStencilShapes, StencilBackground, stencilGroups } from '../config/stencil';
import * as appShapes from '../shapes/app-shapes';

export class StencilService {
  constructor(stencilContainer, isDarkThemeUsed) {
    this.stencilContainer = stencilContainer;
    this.isDarkThemeUsed = isDarkThemeUsed;
  }

  create(paperScroller, snaplines, readOnly = false) {
    if (readOnly) {
      this.stencilContainer.style.display = 'none';
      return;
    }
    this.stencilContainer.style.display = 'block';

    this.stencil = new ui.Stencil({
      label: 'Palette',
      paper: paperScroller,
      snaplines,
      width: 240,
      height: null,
      groups: stencilGroups,
      dropAnimation: !readOnly,
      groupsToggleButtons: true,
      el: this.stencilContainer,
      dragStartClone: readOnly ? null : (cell) => this.handleDragStartClone(cell),
      paperOptions: this.getPaperOptions(),
      layout: this.getStencilLayout(), // <- extracted into method for clarity
    });

    this.stencil.render();
    this.initializeTooltip(); // <- moved method call group together for init logic clarity
    this.startHoverListener();
  }

  getPaperOptions() {
    return () => ({
      model: new dia.Graph({}, { cellNamespace: appShapes }),
      cellViewNamespace: appShapes,
    });
  } // <- abstracted paperOptions for clarity and testability

  getStencilLayout() {
    return {
      columns: 2,
      marginX: 5,
      marginY: 5,
      columnGap: 15,
      rowGap: 15,
      rowHeight: 20,
      columnWidth: 80,
      resizeToFit: true,
    };
  } // <- same as above; isolates config from main method

  handleDragStartClone(cell) {
    const clone = cell.clone();

    if (clone.get('type') === 'appShapes.Tri') {
      clone.resize(100, 60);
    }
    if (clone.get('type') === 'appShapes.Stock') {
      clone.resize(30, 30);
    }
    clone.attr('label/fontSize', 6);
    clone.prop('customAttributes/cellName', '');

    return clone;
  } // <- added type to `cell` for readability and IDE support

  createFromStencilElement(el) {
    const clone = el.clone();
    clone.prop(clone.get('targetAttributes'));
    clone.removeProp('targetAttributes');
    return clone;
  } // <- made  since it's only used internally

  initializeTooltip() {
    this.tooltipGraph = new dia.Graph({}, { cellNamespace: appShapes });
    this.tooltipPaper = new dia.Paper({
      model: this.tooltipGraph,
      cellViewNamespace: appShapes,
      width: 100,
      height: 50,
      async: true,
      autoFreeze: false,
      overflow: true,
      sorting: dia.Paper.sorting.NONE,
    });

    this.tooltip = new ui.Tooltip({
      target: '[model-id]',
      rootTarget: this.stencil.el,
      container: this.stencilContainer,
      content: (el) => this.buildTooltipContent(el),
      position: ui.Tooltip.TooltipPosition.Left,
      positionSelector: '.stencil-container',
      viewport: { selector: '#map-and-panels-container' },
      animation: { duration: '250ms' },
    });

    this.stencil.on({
      'element:dragstart': () => this.tooltip.disable(),
      'element:dragend': () => this.tooltip.enable(),
    });
  }

  buildTooltipContent(el) {
    const modelId = el.getAttribute('model-id');
    if (!modelId) return false;

    const stencilElement = this.findStencilElement(modelId);
    if (!stencilElement) return false;

    const clone = this.createFromStencilElement(stencilElement);
    this.tooltipGraph.resetCells([clone]);

    const shapeNameEl = document.createElement('span');
    shapeNameEl.textContent = stencilElement.get('name');
    shapeNameEl.style.color = 'white';
    const fragment = document.createDocumentFragment();
    fragment.append(this.tooltipPaper.el, shapeNameEl);

    this.tooltipPaper.transformToFitContent({
      padding: 5,
      contentArea: clone.getBBox(),
      verticalAlign: 'middle',
      horizontalAlign: 'middle',
    });

    return fragment;
  }

  findStencilElement(modelId) {
    for (const group of Object.keys(stencilGroups)) {
      const graph = this.stencil.getGraph(group);
      const found = graph.getCell(modelId);
      if (found) return found;
    }
    return null;
  } // <- extracted logic from buildTooltipContent for SRP

  startHoverListener() {
    this.stencil.on({
      'group:element:mouseleave': (groupPaper) => {
        StencilBackground.removeAll(groupPaper);
      },
      'group:element:pointerdown': (groupPaper) => {
        StencilBackground.removeAll(groupPaper);
      },
    });
  }

  setShapes() {
    if (this.stencilContainer.style.display === 'none') {
      return;
    }
    const shapes = getStencilShapes(this.isDarkThemeUsed);
    this.stencil.load(shapes);
  }
}
